import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Activity, Globe } from "lucide-react";
import { API, BACKEND } from "@/lib/api";


export default function Portal() {
    const { slug } = useParams();
    const [data, setData] = useState(null);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/portal/${slug}`);
                if (r.status === 404) {
                    setMissing(true);
                    return;
                }
                if (r.ok) setData(await r.json());
            } catch {
                setMissing(true);
            }
        })();
    }, [slug]);

    const accent = data?.color || "#06B6D4";

    return (
        <div data-testid="portal-page" className="min-h-screen bg-[#0B0F17] text-slate-200">
            <header
                className="border-b border-slate-800/80"
                style={{ boxShadow: `inset 0 2px 0 ${accent}` }}
            >
                <div className="mx-auto flex h-16 max-w-4xl items-center gap-2.5 px-5">
                    <Globe className="h-4 w-4" style={{ color: accent }} />
                    <span data-testid="portal-brand" className="font-display text-sm font-bold">
                        {data?.brand || "…"}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                        webhook portal
                    </span>
                </div>
            </header>

            <main className="mx-auto max-w-4xl px-5 py-10">
                {missing ? (
                    <div className="py-20 text-center">
                        <h1
                            data-testid="portal-not-found"
                            className="font-display text-xl font-bold text-slate-100"
                        >
                            Portal not found
                        </h1>
                        <p className="mt-2 font-mono text-[11px] text-slate-500">
                            This portal is private or does not exist.
                        </p>
                    </div>
                ) : !data ? (
                    <p className="py-20 text-center font-mono text-xs text-slate-500">
                        Loading portal…
                    </p>
                ) : (
                    <>
                        <section>
                            <h2 className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                <Activity className="h-3 w-3" style={{ color: accent }} />
                                Your endpoints
                            </h2>
                            <div className="mt-3 overflow-hidden rounded-xl border border-slate-800/80 bg-[#111827]">
                                <table className="w-full text-left font-mono text-[11px]">
                                    <thead>
                                        <tr className="border-b border-slate-800 text-[9px] uppercase tracking-[0.2em] text-slate-500">
                                            <th className="px-4 py-3">URL</th>
                                            <th className="hidden px-4 py-3 sm:table-cell">
                                                Events
                                            </th>
                                            <th className="px-4 py-3">Health</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.endpoints.map((ep) => (
                                            <tr
                                                key={ep.ep_id}
                                                data-testid={`portal-endpoint-${ep.ep_id}`}
                                                className="border-b border-slate-800/60 last:border-0"
                                            >
                                                <td className="max-w-[220px] truncate px-4 py-3 text-slate-300">
                                                    {ep.url}
                                                </td>
                                                <td className="hidden px-4 py-3 text-slate-500 sm:table-cell">
                                                    {(ep.events || []).length
                                                        ? ep.events.join(", ")
                                                        : "all events"}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {ep.health?.success_rate == null ? (
                                                        <span className="text-slate-600">
                                                            no deliveries
                                                        </span>
                                                    ) : (
                                                        <span
                                                            style={{
                                                                color:
                                                                    ep.health.success_rate >= 95
                                                                        ? "#34D399"
                                                                        : "#FBBF24",
                                                            }}
                                                        >
                                                            {ep.health.success_rate}% ok
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {data.endpoints.length === 0 && (
                                            <tr>
                                                <td
                                                    colSpan={3}
                                                    className="px-4 py-8 text-center text-slate-600"
                                                >
                                                    No endpoints registered yet.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </section>

                        <section className="mt-8">
                            <h2 className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                                Recent deliveries
                            </h2>
                            <div className="mt-3 space-y-1.5">
                                {data.deliveries.length === 0 && (
                                    <p className="rounded-lg border border-dashed border-slate-800 py-8 text-center font-mono text-[11px] text-slate-600">
                                        No deliveries yet.
                                    </p>
                                )}
                                {data.deliveries.map((d) => (
                                    <div
                                        key={d.id}
                                        data-testid={`portal-delivery-${d.id}`}
                                        className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800/80 bg-[#111827] px-4 py-2.5 font-mono text-[11px]"
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <span className="text-slate-600">
                                                {(d.created_at || "").slice(0, 19).replace("T", " ")}
                                            </span>
                                            <span className="text-slate-300">{d.event_type}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {d.latency != null && (
                                                <span className="text-slate-600">{d.latency}ms</span>
                                            )}
                                            <span
                                                className={`rounded border px-1.5 py-px text-[9px] ${
                                                    d.status == null
                                                        ? "border-slate-700 text-slate-500"
                                                        : d.status < 400
                                                          ? "border-emerald-500/30 bg-emerald-950/40 text-emerald-300"
                                                          : "border-rose-500/30 bg-rose-950/40 text-rose-300"
                                                }`}
                                            >
                                                {d.status == null
                                                    ? "pending"
                                                    : d.status === 599
                                                      ? "unreachable"
                                                      : d.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <p className="mt-10 text-center font-mono text-[10px] text-slate-600">
                            Powered by MDH — webhooks with guaranteed delivery
                        </p>
                    </>
                )}
            </main>
        </div>
    );
}
